/**
 * Cmarkdownxychart — main SVG canvas
 *
 * The visual heart of the chart. Renders bars, lines, area fills, axes,
 * grid, and the hover crosshair. Forwards a ref to the <svg> element so the
 * PNG snapshot util can serialize it.
 */
"use client";
import { forwardRef, type MouseEvent, useCallback, useMemo } from "react";
import { XYCHART_BASELINE_COLOR } from "./xychart-constants";
import type { XYChartLayout } from "./xychart-types";
import { XYChartAxisTitles } from "./XYChartAxisTitles";
import { XYChartBarsLayer, type XYChartBarHoverInput } from "./XYChartBarsLayer";
import { XYChartCanvasDefs } from "./XYChartCanvasDefs";
import { XYChartCategoryHitZones } from "./XYChartCategoryHitZones";
import { XYChartCategoryLabels } from "./XYChartCategoryLabels";
import { XYChartCrosshair } from "./XYChartCrosshair";
import { XYChartGridLines } from "./XYChartGridLines";
import { XYChartLineSeriesLayer } from "./XYChartLineSeriesLayer";
import { XYChartTickLabels } from "./XYChartTickLabels";
import { useXYChartLinePathLengths } from "./useXYChartLinePathLengths";
import { createCanvasSvgStyle, createSafeSvgId, resolveHoveredCategoryIndex, selectBarSeries, selectFocusBand, selectLineSeries, selectSeriesOpacity, } from "./xychart-canvas-model";
export interface XYChartCanvasProps {
    layout: XYChartLayout;
    uniqueId: string;
    hoveredSeriesIndex: number | null;
    hoveredCategoryIndex: number | null;
    onHoverSeries: (seriesIndex: number | null) => void;
    onHoverCategory: (categoryIndex: number | null) => void;
}
export const XYChartCanvas = forwardRef<SVGSVGElement, XYChartCanvasProps>(function XYChartCanvas(props, ref) {
    const { layout, uniqueId, hoveredSeriesIndex, hoveredCategoryIndex, onHoverSeries, onHoverCategory, } = props;
    const safeId = createSafeSvgId({ uniqueId });
    const layoutSeries = layout.series;
    const layoutCategoryCenters = layout.categoryCenters;
    const { pathLengths, setLinePathNode } = useXYChartLinePathLengths({
        series: layoutSeries,
    });
    const barSeries = useMemo(() => selectBarSeries({ series: layoutSeries }), [layoutSeries]);
    const lineSeries = useMemo(() => selectLineSeries({ series: layoutSeries }), [layoutSeries]);
    const focusBand = useMemo(() => selectFocusBand({
        hoveredCategoryIndex,
        categoryCenters: layoutCategoryCenters,
    }), [hoveredCategoryIndex, layoutCategoryCenters]);
    const svgStyle = useMemo(() => createCanvasSvgStyle({ layout }), [layout]);
    const seriesOpacityFor = useCallback((seriesIndex: number): number => selectSeriesOpacity({ hoveredSeriesIndex, seriesIndex }), [hoveredSeriesIndex]);
    const handleBarHover = useCallback(({ seriesIndex, categoryIndex }: XYChartBarHoverInput): void => {
        onHoverSeries(seriesIndex);
        onHoverCategory(categoryIndex);
    }, [onHoverCategory, onHoverSeries]);
    const handleMouseLeave = useCallback((): void => {
        onHoverSeries(null);
        onHoverCategory(null);
    }, [onHoverCategory, onHoverSeries]);
    const handleMouseMove = useCallback((event: MouseEvent<SVGSVGElement>): void => {
        const nearestIndex = resolveHoveredCategoryIndex({ event, layout });
        if (nearestIndex == null)
            return;
        if (hoveredCategoryIndex !== nearestIndex) {
            onHoverCategory(nearestIndex);
        }
    }, [hoveredCategoryIndex, layout, onHoverCategory]);
    return (
        <svg data-el="Cmarkdownxychart/Canvas/Root" ref={ref} width={layout.width} height={layout.height} viewBox={`0 0 ${layout.width} ${layout.height}`} style={svgStyle} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
          <XYChartCanvasDefs layout={layout} safeId={safeId}/>

          <g data-el="Cmarkdownxychart/Canvas/Plot" transform={`translate(${layout.margins.left} ${layout.margins.top})`}>
            <XYChartGridLines layout={layout}/>
            <XYChartBarsLayer series={barSeries} safeId={safeId} horizontal={layout.horizontal} seriesOpacityFor={seriesOpacityFor} onHoverBar={handleBarHover}/>
            <XYChartLineSeriesLayer series={lineSeries} safeId={safeId} hoveredCategoryIndex={hoveredCategoryIndex} pathLengths={pathLengths} seriesOpacityFor={seriesOpacityFor} onLinePathNode={setLinePathNode}/>
            <XYChartCrosshair layout={layout} focusBand={focusBand}/>
            <line x1={0} y1={layout.plotHeight} x2={layout.plotWidth} y2={layout.plotHeight} stroke={XYCHART_BASELINE_COLOR} strokeWidth={1}/>
            <XYChartCategoryLabels layout={layout}/>
            <XYChartTickLabels layout={layout}/>
            <XYChartAxisTitles layout={layout}/>
            <XYChartCategoryHitZones layout={layout} onHoverCategory={onHoverCategory}/>
          </g>
        </svg>
      );
});
